import { useCallback, useState } from "react";
import { getTaxIdInformation } from "../services/payments";
import { useUserState } from "../state/user";
import { TaxIdRecord } from "../types/payments";
import { FISCAL_ID_LENGTH, MAX_WAIT_TAX_ID_INFO_MS } from "../constants";

/**
 * Looks a company up by its tax id so the configuration form can prefill
 * itself.
 *
 * This is best effort: the lookup races against a short timeout and any
 * timeout, miss or failure resolves to null. It must never block the form or
 * surface an error — the professional can always type the details in.
 */
export default function useTaxIdAutofill() {
  const { token } = useUserState();
  const [loading, setLoading] = useState(false);

  const lookup = useCallback(
    async (taxId: string): Promise<TaxIdRecord | null> => {
      if (!token || taxId.length !== FISCAL_ID_LENGTH) return null;

      const timeout = new Promise<null>(resolve =>
        setTimeout(() => resolve(null), MAX_WAIT_TAX_ID_INFO_MS)
      );

      try {
        setLoading(true);
        return await Promise.race([
          timeout,
          getTaxIdInformation({ authToken: token, taxId })
        ]);
      } catch {
        // A failed lookup is indistinguishable from an unknown tax id here.
        return null;
      } finally {
        setLoading(false);
      }
    },
    [token]
  );

  return { lookup, loading };
}
